import { format, isSameDay } from "date-fns";
import { formatShortTimeAgo } from "@/lib/format-utils";
import type { Automation, AutomationIdentityRecord } from "./model";

/** `19:03` today, `2 Nov 09:00` otherwise. */
export function formatStampTime(at: number, now: number): string {
	return isSameDay(at, now) ? format(at, "HH:mm") : format(at, "d MMM HH:mm");
}

export function parseStamp(value: string | null | undefined): number | null {
	if (!value) return null;
	const time = new Date(value).getTime();
	return Number.isFinite(time) ? time : null;
}

export function latestStamp(...values: (number | null)[]): number | null {
	const known = values.filter((value): value is number => value != null);
	return known.length > 0 ? Math.max(...known) : null;
}

type AutomationStatus = {
	/** One plain-language line: the problem, what runs now, or the last run. */
	text: string;
	/** A failed last run or a server-flagged degradation. */
	problem: boolean;
	/** The newest moment the Automation did anything. */
	at: number | null;
};

/**
 * How an Automation stands, read the same way wherever it is listed (Home,
 * Settings › Automations): a failed run first, then a due run nobody picked
 * up, a running or queued run, the server's health reason, and otherwise the
 * last run or the next one.
 */
export function automationStatus(
	automation: Automation<AutomationIdentityRecord>,
	now: number,
): AutomationStatus {
	const record = automation.automation;
	const runStatus = record.automation_run_status ?? null;
	const runCreated = parseStamp(record.automation_run_created_at);
	const runCompleted = parseStamp(record.automation_run_completed_at);
	// The materialized completion stamp wins; `last_fired_at` is stamped by only
	// one terminal writer, so it stays the fallback for a run that never
	// completed.
	const lastRan =
		parseStamp(record.last_run_completed_at) ??
		runCompleted ??
		parseStamp(record.last_fired_at);
	const nextRun = parseStamp(record.next_run_at);
	const failed =
		runStatus === "failed" ||
		runStatus === "timeout" ||
		runStatus === "cancelled";
	// Due and still not picked up: the schedule passed and no run started since.
	const missed =
		nextRun != null &&
		nextRun < now &&
		(runCreated == null || runCreated < nextRun) &&
		runStatus !== "running" &&
		runStatus !== "claimed" &&
		runStatus !== "pending";
	const runAt = runCompleted ?? runCreated;
	let text: string;
	if (failed && runAt != null) {
		const outcome = runStatus === "timeout" ? "timed out" : runStatus;
		const reason = record.automation_run_error?.trim();
		text = `Last run ${formatStampTime(runAt, now)} · ${outcome}${reason ? `: ${reason}` : ""}`;
	} else if (missed && nextRun != null) {
		text = `Due ${formatShortTimeAgo(nextRun)}, not picked up`;
	} else if (runStatus === "running" || runStatus === "claimed") {
		text = `Running since ${formatStampTime(runCreated ?? now, now)}`;
	} else if (runStatus === "pending") {
		text = `Queued ${formatStampTime(runCreated ?? now, now)}`;
	} else if (record.health === "degraded" && record.health_reasons?.[0]) {
		text = record.health_reasons[0];
	} else if (lastRan != null) {
		text = `Ran ${formatStampTime(lastRan, now)}`;
	} else if (nextRun != null) {
		text = `Next run ${formatStampTime(nextRun, now)}`;
	} else if (automation.output.destination === "source") {
		text = "Replies to incoming messages";
	} else {
		text = "Never run";
	}
	return {
		text,
		// Overdue is the server's call (one missed interval, at least an hour):
		// it degrades health and sends the "has not run" notice. A due time just
		// passed is not a problem yet.
		problem: failed || record.health === "degraded",
		at: latestStamp(runCompleted, runCreated, lastRan),
	};
}
